// Claude: SellerSubscriptionPlanModal — Add/Edit plan modal extracted from Subscriptions.tsx 10-Apr-2026
// Claude: removed unused fields (emailSearch, relevanceMapping, notesFeature, meetingType, promoteTopListing, isChatAllowed, isAnnouncementsAllowed) 10-Apr-2026
// Claude: rfxResponsePendingLimit, subscriptionValue, currency added; fields reordered 20-04-2026
// Claude: rfxInterestedLimit removed 24-04-2026
// Claude: viewOnly prop — read-only mode when sellers are associated with plan 26-04-2026
import { Box, Button, Flex, Grid, GridItem, Input, Switch, Text } from "@chakra-ui/react";
import { Modal } from "antd";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setDigitalAssetsLimit,
  setSellerTagsLimit,
  setIsActive,
  setPendingMeetingsLimit,
  setPlanName,
  setRfxLimit,
  setRfxResponsePendingLimit,
  setSubscriptionValue,
  setCurrency,
} from "../../../redux/slices/sellerSubscriptionSlice";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  edit: boolean;
  onSubmit: () => void;
  loading: boolean;
  viewOnly?: boolean;
};

const SellerSubscriptionPlanModal = ({ isOpen, onClose, edit, onSubmit, loading, viewOnly }: Props) => {
  const dispatch = useDispatch();
  const { subscriptions, ...planDetails } = useSelector((state: any) => state.sellerSubscription);

  return (
    <Modal
      footer={null}
      title={viewOnly ? "View Seller Subscription Plan" : edit ? "Edit Seller Subscription Plan" : "Add Seller Subscription Plan"}
      width={"30%"}
      open={isOpen}
      onCancel={onClose}
      onOk={onSubmit}
      centered
      bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
    >
      <Box padding={2}>
        <Grid gap={3} templateColumns="repeat(1, 1fr)">

          {/* 1. Plan Name */}
          <GridItem colSpan={1}>
            <Box display={"flex"} flexDirection={"row"} gap={"8px"} justifyContent={"space-between"}>
              <Text fontSize={"xs"} fontWeight={"500"}>
                Plan Name <span style={{ color: "red" }}>*</span>
              </Text>
              <Input
                width={"50%"} height="30px" fontSize="xs"
                placeholder="Enter Plan Name"
                value={planDetails.planName}
                onChange={(e) => { dispatch(setPlanName(e.target.value)); }}
                isReadOnly={viewOnly} bg={viewOnly ? "gray.50" : undefined}
              />
            </Box>
          </GridItem>

          {/* 2. Is Active */}
          <GridItem colSpan={1}>
            <Box display={"flex"} flexDirection={"row"} gap={"8px"} justifyContent={"space-between"} alignItems={"center"}>
              <Text fontSize={"xs"} fontWeight={"500"}>
                Active Status <span style={{ color: "red" }}>*</span>
              </Text>
              <Box width={"50%"}>
                <Switch
                  size="sm"
                  isChecked={planDetails.isActive}
                  onChange={() => { dispatch(setIsActive(!planDetails.isActive)); }}
                  isDisabled={viewOnly}
                />
              </Box>
            </Box>
          </GridItem>

          {/* 3. Subscription Value + Currency side by side */}
          <GridItem colSpan={1}>
            <Flex gap={2} alignItems={"center"}>
              <Text fontSize={"xs"} fontWeight={"500"} flex={1}>Subscription Value</Text>
              <Input
                width={"30%"} height="30px" fontSize="xs" type="number"
                placeholder="0"
                value={planDetails.subscriptionValue}
                onChange={(e) => { dispatch(setSubscriptionValue(e.target.value)); }}
                isReadOnly={viewOnly} bg={viewOnly ? "gray.50" : undefined}
              />
              <Input
                width={"20%"} height="30px" fontSize="xs"
                placeholder="USD"
                value={planDetails.currency}
                onChange={(e) => { dispatch(setCurrency(e.target.value)); }}
                isReadOnly={viewOnly} bg={viewOnly ? "gray.50" : undefined}
              />
            </Flex>
          </GridItem>

          {/* 4. RFX Opportunities Limit */}
          <GridItem colSpan={1}>
            <Box display={"flex"} flexDirection={"row"} gap={"8px"} justifyContent={"space-between"}>
              <Box>
                <Text fontSize={"xs"} fontWeight={"500"}>RFX Opportunities Limit <span style={{ color: "red" }}>*</span></Text>
                <Text fontSize={"10px"} color={"gray.400"}>Enter -1 for unlimited</Text>
              </Box>
              <Input
                width={"50%"} height="30px" fontSize="xs"
                placeholder="e.g. 50"
                value={planDetails.rfxLimit}
                onChange={(e) => { dispatch(setRfxLimit(e.target.value)); }}
                isReadOnly={viewOnly} bg={viewOnly ? "gray.50" : undefined}
              />
            </Box>
          </GridItem>

          {/* 5. RFX Response Pending Limit */}
          <GridItem colSpan={1}>
            <Box display={"flex"} flexDirection={"row"} gap={"8px"} justifyContent={"space-between"}>
              <Box>
                <Text fontSize={"xs"} fontWeight={"500"}>RFX Response Pending Limit</Text>
                <Text fontSize={"10px"} color={"gray.400"}>Enter -1 for unlimited</Text>
              </Box>
              <Input
                width={"50%"} height="30px" fontSize="xs"
                placeholder="e.g. 10"
                value={planDetails.rfxResponsePendingLimit}
                onChange={(e) => { dispatch(setRfxResponsePendingLimit(e.target.value)); }}
                isReadOnly={viewOnly} bg={viewOnly ? "gray.50" : undefined}
              />
            </Box>
          </GridItem>

          {/* 7. Pending Meetings Limit */}
          <GridItem colSpan={1}>
            <Box display={"flex"} flexDirection={"row"} gap={"8px"} justifyContent={"space-between"}>
              <Box>
                <Text fontSize={"xs"} fontWeight={"500"}>Pending Meetings Limit <span style={{ color: "red" }}>*</span></Text>
                <Text fontSize={"10px"} color={"gray.400"}>Enter -1 for unlimited</Text>
              </Box>
              <Input
                width={"50%"} height="30px" fontSize="xs"
                placeholder="e.g. 5"
                value={planDetails.pendingMeetingsLimit}
                onChange={(e) => { dispatch(setPendingMeetingsLimit(e.target.value)); }}
                isReadOnly={viewOnly} bg={viewOnly ? "gray.50" : undefined}
              />
            </Box>
          </GridItem>

          {/* 8. Tags Limit — admin only, not shown to seller */}
          <GridItem colSpan={1}>
            <Box display={"flex"} flexDirection={"row"} gap={"8px"} justifyContent={"space-between"}>
              <Box>
                <Text fontSize={"xs"} fontWeight={"500"}>Seller Tags Limit <span style={{ color: "red" }}>*</span></Text>
                <Text fontSize={"10px"} color={"gray.400"}>Admin only · Enter -1 for unlimited</Text>
              </Box>
              <Input
                width={"50%"} height="30px" fontSize="xs"
                placeholder="e.g. 10"
                value={planDetails.sellerTagsLimit}
                onChange={(e) => { dispatch(setSellerTagsLimit(e.target.value)); }}
                isReadOnly={viewOnly} bg={viewOnly ? "gray.50" : undefined}
              />
            </Box>
          </GridItem>

          {/* 9. Digital Assets Limit — admin only, not shown to seller */}
          <GridItem colSpan={1}>
            <Box display={"flex"} flexDirection={"row"} gap={"8px"} justifyContent={"space-between"}>
              <Box>
                <Text fontSize={"xs"} fontWeight={"500"}>Digital Assets Limit <span style={{ color: "red" }}>*</span></Text>
                <Text fontSize={"10px"} color={"gray.400"}>Admin only · Enter -1 for unlimited</Text>
              </Box>
              <Input
                width={"50%"} height="30px" fontSize="xs"
                placeholder="e.g. 5"
                value={planDetails.digitalAssetsLimit}
                onChange={(e) => { dispatch(setDigitalAssetsLimit(e.target.value)); }}
                isReadOnly={viewOnly} bg={viewOnly ? "gray.50" : undefined}
              />
            </Box>
          </GridItem>

        </Grid>

        {!viewOnly && (
          <Box mt={10}>
            <Flex justifyContent={"end"}>
              <Button
                h={"40px"} background={"primary.main"} color={"white"} px={3} gap={2}
                _hover={{ background: "#2E7D32", color: "white" }}
                isLoading={loading}
                onClick={onSubmit}
              >
                Submit
              </Button>
            </Flex>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default SellerSubscriptionPlanModal;
